import { useState, useEffect, useRef } from 'react';

export const useNumericFieldLogic = ({
  id,
  value,
  onChange,
  readOnly = false,
  currency = false,
  percentage = false,
  min,
  max
}) => {
  const [digits, setDigits] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  // Formato para moeda brasileira (R$)
  const formatAsCurrency = (value) => {
    // código original mantido...
    if (!value && value !== '0' && value !== 0) return 'R$ 0,00';
    const numValue = typeof value === 'string' ?
      parseFloat(value.replace(',', '.')) : parseFloat(value);
    if (isNaN(numValue)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(numValue);
  };

  // Formato para porcentagem
  const formatAsPercentage = (value) => {
    // código original mantido...
    if (!value && value !== '0' && value !== 0) return '0,00%';
    const numValue = typeof value === 'string' ?
      parseFloat(value.replace(',', '.')) : parseFloat(value);
    if (isNaN(numValue)) return '0,00%';
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue / 100);
  };

  // Validar valor com base em min e max
  const validateValue = (numericValue) => {
    let validatedValue = numericValue;
    if (min !== undefined && !isNaN(parseFloat(min)) && validatedValue < parseFloat(min)) {
      validatedValue = parseFloat(min);
    }
    if (max !== undefined && !isNaN(parseFloat(max)) && validatedValue > parseFloat(max)) {
      validatedValue = parseFloat(max);
    }
    return validatedValue;
  };

  // Manipula entrada para moeda/porcentagem
  const handleDigitsInput = (e) => {
    // código original mantido...
    if (readOnly) return;
    setIsEditing(true);
    const input = e.target.value;
    const cleanInput = input.replace(/\D/g, '');
    if (!cleanInput) {
      const minValue = validateValue(0);
      if (minValue > 0) {
        setDigits(Math.round(minValue * 100).toString());
        onChange({ target: { id, value: minValue } });
      } else {
        setDigits('');
        onChange({ target: { id, value: '' } });
      }
      return;
    }
    setDigits(cleanInput);
    let numericValue = parseInt(cleanInput) / 100;
    
    onChange({ target: { id, value: numericValue } });
  };

  const handleStandardInput = (e) => {
    if (readOnly) return;

    setIsEditing(true);
    const inputValue = e.target.value;

    // Se o campo estiver vazio, apenas retorne o valor como está
    if (inputValue === '') {
      onChange(e);
      return;
    }

    // Converte para número, aceitando tanto ponto quanto vírgula
    const numValue = parseFloat(inputValue.replace(',', '.'));

    // Se não for um número válido, apenas retorne o valor como está
    if (isNaN(numValue)) {
      onChange(e);
      return;
    }

    // Valida o valor com base em min e max
    const validatedValue = validateValue(numValue);

    // Notifica com o valor validado
    onChange({
      ...e,
      target: {
        ...e.target,
        id,
        value: validatedValue.toString()
      }
    });
  };

  // Quando o campo perde o foco
  const handleBlur = () => {
    // código original mantido...
    setIsEditing(false);
    if (value !== undefined && value !== '') {
      const numericValue = parseFloat(value);
      if (!isNaN(numericValue)) {
        const validatedValue = validateValue(numericValue);
        if (validatedValue !== numericValue) {
          if (currency || percentage) {
            setDigits(Math.round(validatedValue * 100).toString());
          }
          onChange({ target: { id, value: validatedValue } });
        }
      }
    }
  };

  // Efeito para sincronizar com props externos
  useEffect(() => {
    // código original mantido...
    if (!isEditing && value !== undefined) {
      const valueAsNumber = parseFloat(value);
      if (!isNaN(valueAsNumber)) {
        if (currency || percentage) {
          const valueAsDigits = Math.round(valueAsNumber * 100).toString();
          if (digits !== valueAsDigits) {
            setDigits(valueAsDigits);
          }
        }
      }
    }
  }, [value, currency, percentage, isEditing, digits]);

  // Efeito para ajustar valor se max mudar
  useEffect(() => {
    if (max !== undefined && value !== undefined && !isNaN(parseFloat(max)) && !isNaN(parseFloat(value))) {
      const maxValue = parseFloat(max);
      const currentValue = parseFloat(value);
      if (currentValue > maxValue) {
        onChange({ target: { id, value: maxValue } });
      }
    }
  }, [max]);

  // Calcula o valor de exibição
  const getDisplayValue = () => {
    if (currency) {
      return formatAsCurrency(parseFloat(digits) / 100);
    } else if (percentage) {
      return formatAsPercentage(parseFloat(digits) / 100);
    }
    return value;
  };

  return {
    inputRef,
    displayValue: getDisplayValue(),
    handleDigitsInput,
    handleStandardInput,
    handleBlur,
    setIsEditing
  };
};